import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import Language, { Languages, Locale } from "src/constants/language";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { featureFlags } from "./featureFlags";
import reportErrorSentry from "./reportErrorSentry";
import localeEs from "dayjs/locale/es";
import translationEnglish from "src/translations/en.json";
import translationSpanish from "src/translations/es.json";
import dayjs from "dayjs";
import { Blac } from "blac-next";
import registerTranslatedZodErrorMap from "src/lib/zodTranslatedErrorMap";

export const getSupportedUserLanguage = (): Language => {
  const sessionOverwrite = sessionStorage.getItem("9am.initialLanguage");
  const userPreferences = Blac.getBloc(UserPreferencesCubit);
  const userLangList = [
    sessionOverwrite,
    userPreferences.state[UserPreferenceKeys.language],
    ...navigator.languages
  ];

  const userLangShortList = userLangList.map((lang) =>
    lang?.toLowerCase().slice(0, 2)
  );

  const userLangSupported = userLangShortList.find(
    (lang) => Languages.indexOf(lang as Language) !== -1
  ) as Language | undefined;

  return userLangSupported ?? Languages[0];
};

export const getSupportedUserLocale = (): Locale => {
  const userLanguage = getSupportedUserLanguage();
  return Locale[userLanguage] ?? Locale.en;
};

export const initializeLocalisation = async (
  locale: Locale = getSupportedUserLocale(),
  translationSupported = featureFlags.translationActive
): Promise<void> => {
  registerTranslatedZodErrorMap();

  const lng = translationSupported
    ? ((locale ?? Language.en).slice(0, 2) as Language)
    : Language.en;
  const currentLanguage = i18next.language;

  if (currentLanguage === lng) {
    return;
  }

  if (Languages.indexOf(lng) === -1) {
    throw new Error(`Language "${locale}" not supported`);
  }

  window.nineConfig = {
    ...window.nineConfig,
    language: lng
  };

  dayjs.locale(locale === Locale.es ? localeEs : "en");

  await i18next.use(initReactI18next).init({
    resources: {
      en: {
        translation: translationEnglish
      },
      es: {
        translation: translationSpanish
      }
    },
    lng,
    fallbackLng: "en",
    interpolation: {
      escapeValue: false
    }
  });
};

export const changeLanguage = async (locale: Locale | string) => {
  if (!i18next.isInitialized) {
    await initializeLocalisation();
  }

  const sessionOverwrite = sessionStorage.getItem("9am.initialLanguage");
  const isString = typeof locale === "string";
  const language = sessionOverwrite ?? (isString ? locale.slice(0, 2) : "en");
  const lng =
    Languages.indexOf(language as Language) !== -1 ? language : Languages[0];
  window.nineConfig = {
    ...window.nineConfig,
    language: lng
  };

  dayjs.locale(locale === Locale.es ? localeEs : "en");

  await i18next.changeLanguage(lng, (err) => {
    if (err) {
      reportErrorSentry(err);
    }
  });
};

export const getTranslationKeys = (): string[] => {
  const keys = Object.keys(translationEnglish);
  return keys;
};
